import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { useSiteContent } from "@/hooks/useSiteContent";
import { outlets as staticOutlets } from "@/assets/outlets";

const MediaMarquee = () => {
  const { t, isRTL, lang } = useLanguage();
  const headerRef = useReveal();
  const gridRef = useReveal({ threshold: 0.05 });
  const { data: doc } = useSiteContent("outlets");

  const heading = (lang === "ar" ? doc?.heading_ar : doc?.heading_en) || t.media.heading;
  const outlets =
    Array.isArray(doc?.items) && doc.items.length
      ? doc.items.map((it, i) => ({
          id: it.id || `dyn-${i}`,
          name: it.name,
          image: it.image,
        }))
      : staticOutlets;

  return (
    <section
      id="media"
      data-testid="media-section"
      className="relative section-y bg-ink overflow-hidden border-t border-white/5"
    >
      <div className="container-x">
        <div ref={headerRef} className="reveal max-w-3xl mb-16 lg:mb-20">
          <div className="flex items-center gap-4 mb-6">
            <span className="hairline" />
            <span className="overline">{t.media.overline}</span>
          </div>
          <h2
            className={`${
              isRTL
                ? "font-arabic font-black leading-[1.15]"
                : "font-serif leading-[1.05]"
            } text-bone text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance`}
            data-testid="media-heading"
          >
            {heading}
          </h2>
        </div>

        {/* Responsive logo grid — all outlets visible at once */}
        <div
          ref={gridRef}
          className="reveal grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 border border-white/10 divide-x divide-y divide-white/10 rtl:divide-x-reverse"
        >
          {outlets.map((o, i) => (
            <div
              key={o.id}
              data-testid={`media-outlet-${i}`}
              className="group relative flex items-center justify-center h-32 sm:h-36 lg:h-40 p-6 bg-ink hover:bg-ink-soft transition-colors duration-500"
            >
              {o.image ? (
                <img
                  src={o.image}
                  alt={o.name || ""}
                  className="max-h-[72%] max-w-[82%] w-auto h-auto object-contain opacity-65 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-500"
                  loading="lazy"
                  draggable="false"
                />
              ) : (
                <span className="font-arabic text-white/50 text-sm">{o.name}</span>
              )}
              <span className="sr-only">{o.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MediaMarquee;
